import { useState } from 'react';
import { B } from '../constants/brand';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Btn, Spinner, ErrorMsg, Card, Badge, Modal, useToast, Icon } from '../components/ui';
import { GovWinIqPanel } from '../components/shared/GovWinIqPanel';
import { formatInstant } from '../utils/format';

// Local datetime formatter — fmt has date-only, and captures need the time.
const dt = (v) => {
  if (!v) return '—';
  return formatInstant(v) || String(v);
};

const outcomeColor = (o) => {
  if (o === 'imported')         return B.green;
  if (o === 'no-opportunities') return B.amber;
  if (o === 'error' || o === 'not-found') return B.red;
  return B.navy;
};

// Pretty-print a JSON string (RawJson / ParsedJson columns) with a raw fallback.
const pretty = (s) => {
  if (s == null || s === '') return '';
  try { return JSON.stringify(JSON.parse(s), null, 2); }
  catch { return String(s); }
};

const preStyle = {
  fontSize: 11, fontFamily: 'monospace', background: B.surfaceMuted,
  border: `1px solid ${B.border}`, borderRadius: 6, padding: 10,
  overflowX: 'auto', maxHeight: 360, marginTop: 6, whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

// Detail modal — fetches the full capture (incl. RawJson) on open.
const CaptureModal = ({ capture, onClose }) => {
  const { toast } = useToast();
  const { data, loading, error } = useApi(
    () => api.get(`govwins/email-captures/${capture.captureID}`), [capture.captureID]
  );

  const copy = async (label, text) => {
    try { await navigator.clipboard.writeText(text || ''); toast.success(`${label} copied`); }
    catch { toast.error('Copy failed'); }
  };

  const row = data || capture;
  const rawStr    = pretty(data?.rawJson);
  const parsedStr = pretty(data?.parsedJson);

  return (
    <Modal title="Captured GovWin email" onClose={onClose} width={840}>
      {loading && <Spinner />}
      {error && <ErrorMsg msg={error} />}
      {!loading && (
        <div style={{ fontSize: 13, color: B.text }}>
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '4px 12px', marginBottom: 14 }}>
            <b style={{ color: B.textMd }}>Received</b><span>{dt(row.receivedUTC)}</span>
            <b style={{ color: B.textMd }}>Captured</b><span>{dt(row.createdUTC)}</span>
            <b style={{ color: B.textMd }}>From</b><span>{row.fromAddress || '—'}</span>
            <b style={{ color: B.textMd }}>To</b><span>{row.toAddresses || '—'}</span>
            <b style={{ color: B.textMd }}>Subject</b><span>{row.subject || '—'}</span>
            <b style={{ color: B.textMd }}>Matched by</b><span>{row.matchedBy || '—'}</span>
            <b style={{ color: B.textMd }}>Outcome</b>
            <span><Badge color={outcomeColor(row.outcome)}>{row.outcome || '—'}</Badge></span>
            <b style={{ color: B.textMd }}>Parsed</b>
            <span>{row.opportunitiesParsed ?? 0} opportunities · {row.upserted ?? 0} upserted · {row.inserted ?? 0} new · {row.docsPulled ?? 0} docs</span>
            {row.errorMessage && (<><b style={{ color: B.red }}>Error</b><span style={{ color: B.red }}>{row.errorMessage}</span></>)}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <b style={{ fontSize: 13, color: B.text }}>Raw email (JSON)</b>
            <Btn variant="ghost" size="sm" onClick={() => copy('Raw JSON', data?.rawJson)}>Copy</Btn>
          </div>
          <pre style={preStyle}>{rawStr || '—'}</pre>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
            <b style={{ fontSize: 13, color: B.text }}>Parsed opportunities (JSON)</b>
            <Btn variant="ghost" size="sm" onClick={() => copy('Parsed JSON', data?.parsedJson)}>Copy</Btn>
          </div>
          <pre style={preStyle}>{parsedStr || '—'}</pre>
        </div>
      )}
    </Modal>
  );
};

export const GovWinEmails = () => {
  const [selected, setSelected] = useState(null);
  const { data, loading, error, reload } = useApi(() => api.get('govwins/email-captures'), []);

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  const captures  = data?.captures || [];
  const available = data?.available !== false;

  return (
    <div>
      <GovWinIqPanel />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: B.textMd, maxWidth: 620 }}>
          <b>Forwarded alert emails.</b> Every email routed to the GovWin ingest (forwarded to <b>import+gov@harvus.com</b>, or recognized
          by the GovWin sender/subject) is captured here in full — click a row to view and copy the raw JSON.
        </div>
        <Btn variant="secondary" size="sm" onClick={reload}><Icon name="refresh" size={14} /> Refresh</Btn>
      </div>

      {!available && (
        <Card style={{ padding: 16, fontSize: 13, color: B.textMd }}>
          Capture storage isn’t available yet — run the database migration (<code>133_migration_govwins_email_captures</code>) to start recording GovWin emails.
        </Card>
      )}

      {available && captures.length === 0 && (
        <Card style={{ padding: 16, fontSize: 13, color: B.textMd }}>
          No GovWin emails captured yet. Once one is forwarded to import+gov@harvus.com (or the mailbox is scanned), it’ll appear here.
        </Card>
      )}

      {available && captures.length > 0 && (
        <Card style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: B.surfaceMuted, color: B.textMd, textAlign: 'left' }}>
                <th style={{ padding: '9px 12px' }}>Received</th>
                <th style={{ padding: '9px 12px' }}>From</th>
                <th style={{ padding: '9px 12px' }}>Subject</th>
                <th style={{ padding: '9px 12px' }}>Matched</th>
                <th style={{ padding: '9px 12px' }}>Outcome</th>
                <th style={{ padding: '9px 12px', textAlign: 'right' }}>Parsed</th>
              </tr>
            </thead>
            <tbody>
              {captures.map((c) => (
                <tr
                  key={c.captureID}
                  onClick={() => setSelected(c)}
                  style={{ cursor: 'pointer', borderTop: `1px solid ${B.border}` }}
                >
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', color: B.textMd }}>{dt(c.receivedUTC || c.createdUTC)}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{c.fromAddress || '—'}</td>
                  <td style={{ padding: '9px 12px', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subject || '—'}</td>
                  <td style={{ padding: '9px 12px', color: B.textLt }}>{c.matchedBy || '—'}</td>
                  <td style={{ padding: '9px 12px' }}><Badge color={outcomeColor(c.outcome)}>{c.outcome || '—'}</Badge></td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: B.textMd }}>{c.opportunitiesParsed ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {selected && <CaptureModal capture={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};
